import { Link, useParams, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { DashboardBar, DashboardWidget, FormDashboard } from '../lib/types';
import { Badge, Button, Card, EmptyState, Spinner } from '../components/ui';
import { Icon, type IconName } from '../components/icons';

/**
 * A form's dashboard, drawn from a descriptor the server generates (docs/25).
 *
 * The page this replaces rendered whatever `/summary` happened to aggregate: headings that
 * were refs, bars labelled with stable choice values, and widgets in dictionary order. It
 * used one half of what the server holds — the rollups — and none of the other half, the
 * form's own definition, which is where the titles, the choice labels and the order live.
 *
 * Nothing here decides what a question deserves. The server sends a widget kind and this
 * draws it, so the same descriptor can render to a PDF and a scheduled email later without
 * a second selection rule that drifts out of step.
 */
export function Dashboard() {
  const { formId = '' } = useParams();
  const [params, setParams] = useSearchParams();

  const range = {
    from: params.get('from') ?? undefined,
    to: params.get('to') ?? undefined,
  };

  const dashboard = useQuery({
    queryKey: ['dashboard', formId, range.from ?? '', range.to ?? ''],
    queryFn: () => api.responses.dashboard(formId, range),
  });

  function setRange(from?: string, to?: string) {
    const next = new URLSearchParams();
    if (from) next.set('from', from);
    if (to) next.set('to', to);
    setParams(next, { replace: true });
  }

  if (dashboard.isLoading) {
    return <div className="p-8"><Spinner label="Loading dashboard" /></div>;
  }

  if (!dashboard.data) return null;
  const data = dashboard.data;

  const empty = data.totals.responses === 0 && data.totals.starts === 0;

  return (
    <div className="mx-auto max-w-5xl space-y-5 p-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">{data.form.name}</h1>
          <p className="text-sm text-[var(--ff-text-muted)]">
            {data.range.from
              ? `${data.range.from.slice(0, 10)} to ${data.range.to?.slice(0, 10) ?? 'today'}`
              : 'All time'}
            {data.form.versionNumber != null && ` · version ${data.form.versionNumber}`}
            {data.form.versionsInRange > 1
              && ` · responses span ${data.form.versionsInRange} versions`}
          </p>
        </div>

        <RangePicker active={params.toString()} onPick={setRange} />
      </header>

      {empty ? (
        <Card>
          <EmptyState
            title="Nothing to summarise yet"
            description="Publish the form and share its link. Every question gets its own
                         summary here as responses arrive."
          />
        </Card>
      ) : (
        <>
          <Totals data={data} />
          {data.series.length > 0 && <Series series={data.series} />}
          {data.funnel && <Funnel funnel={data.funnel} />}
          {data.devices.length > 0 && <Devices devices={data.devices} />}

          {data.sections.map((section, index) => (
            <section key={`${section.title}-${index}`} className="space-y-3">
              <div className="border-b border-[var(--ff-border)] pb-1.5">
                <h2 className="text-sm font-semibold">{section.title}</h2>
                {section.description && (
                  <p className="mt-0.5 text-xs text-[var(--ff-text-muted)]">
                    {section.description}
                  </p>
                )}
              </div>

              <div className="space-y-3">
                {section.widgets.map((widget) => (
                  <Widget key={widget.questionRef} widget={widget} formId={formId} />
                ))}
              </div>
            </section>
          ))}
        </>
      )}
    </div>
  );
}

/* ---- header ---------------------------------------------------------------------- */

function RangePicker({
  active, onPick,
}: { active: string; onPick: (from?: string, to?: string) => void }) {
  const presets: { label: string; days?: number }[] = [
    { label: 'All time' },
    { label: '7 days', days: 7 },
    { label: '30 days', days: 30 },
    { label: '90 days', days: 90 },
  ];

  return (
    <div role="group" aria-label="Date range" className="flex gap-1">
      {presets.map((preset) => {
        const from = preset.days ? isoDay(-preset.days + 1) : undefined;
        const to = preset.days ? isoDay(0) : undefined;
        const key = new URLSearchParams(
          from ? { from, to: to! } : {},
        ).toString();

        return (
          <Button
            key={preset.label}
            variant={key === active ? 'primary' : 'ghost'}
            className="min-h-8 px-2.5 py-1 text-xs"
            aria-pressed={key === active}
            onClick={() => onPick(from, to)}
          >
            {preset.label}
          </Button>
        );
      })}
    </div>
  );
}

function Totals({ data }: { data: FormDashboard }) {
  const { totals, freshness } = data;

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {/* Views have no per-day row anywhere, only a running counter, so a date range
            cannot be applied to them. Shown as unavailable rather than as an all-time
            figure sitting beside numbers that are not all-time. */}
        <Stat label="Views" value={totals.views ?? '—'}
              hint={totals.views == null ? 'Not available for a date range' : undefined} />
        <Stat label="Starts" value={totals.starts} />
        <Stat label="Responses" value={totals.responses} />
        <Stat label="Completion" value={`${totals.completionRate}%`} />
      </div>

      {totals.medianDurationMs != null && (
        <Card className="flex flex-wrap gap-x-8 gap-y-2 p-4">
          <div>
            <div className="text-lg font-semibold tabular-nums">
              {Math.round(totals.medianDurationMs / 1000)}s
            </div>
            <div className="text-xs text-[var(--ff-text-muted)]">median time to complete</div>
          </div>
          {totals.p90DurationMs != null && (
            <div>
              <div className="text-lg font-semibold tabular-nums">
                {Math.round(totals.p90DurationMs / 1000)}s
              </div>
              {/* The median describes the typical respondent; the 90th describes the one who
                  nearly gave up, which is the one worth designing for. */}
              <div className="text-xs text-[var(--ff-text-muted)]">90th percentile</div>
            </div>
          )}
        </Card>
      )}

      {freshness.liveResponses > 0 && (
        <p className="text-xs text-[var(--ff-text-muted)]">
          Includes {freshness.liveResponses}{' '}
          {freshness.liveResponses === 1 ? 'response' : 'responses'} the rollup worker has
          not folded in yet. The totals are exact either way.
        </p>
      )}
    </div>
  );
}

function Stat({
  label, value, hint,
}: { label: string; value: number | string; hint?: string }) {
  return (
    <Card className="p-3">
      <div className="text-xs text-[var(--ff-text-muted)]">{label}</div>
      <div className="text-xl font-semibold tabular-nums">{value}</div>
      {hint && <div className="mt-0.5 text-[10px] text-[var(--ff-text-muted)]">{hint}</div>}
    </Card>
  );
}

/**
 * Responses per day, drawn over the starts they came from.
 *
 * Two marks on one scale rather than two scales: every response is also a start, so the
 * filled part is a share of the track and the gap between them is the drop-off. A second
 * y-axis would let the two be scaled independently, which is how a chart is made to say
 * whatever its author wants.
 */
function Series({ series }: { series: FormDashboard['series'] }) {
  const max = Math.max(...series.map((d) => Math.max(d.starts, d.responses)), 1);

  return (
    <Card className="p-4">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-xs font-semibold text-[var(--ff-text-muted)]">Responses per day</h2>
        <div className="flex items-center gap-3 text-[11px] text-[var(--ff-text-muted)]">
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm"
                  style={{ background: 'var(--ff-viz-1)' }} /> Responses
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm"
                  style={{ background: 'var(--ff-viz-track)' }} /> Starts
          </span>
        </div>
      </div>

      <div className="flex items-end gap-1 overflow-x-auto pb-1" style={{ height: 132 }}>
        {series.map((day) => (
          <div key={day.date} className="flex min-w-6 flex-1 flex-col items-center gap-1">
            <span className="text-[10px] tabular-nums text-[var(--ff-text-muted)]">
              {day.responses}
            </span>
            <div
              className="flex w-full items-end justify-center rounded-t"
              style={{
                height: `${Math.max((day.starts / max) * 84, 2)}px`,
                background: 'var(--ff-viz-track)',
              }}
              title={`${day.date}: ${day.responses} of ${day.starts} starts`}
            >
              <div
                className="w-full rounded-t"
                style={{
                  height: `${day.starts > 0 ? (day.responses / day.starts) * 100 : 0}%`,
                  background: 'var(--ff-viz-1)',
                }}
              />
            </div>
            <span className="text-[10px] text-[var(--ff-text-muted)]">{day.date.slice(5)}</span>
          </div>
        ))}
      </div>

      {/* The chart is never the only way to read the numbers (docs/17). */}
      <table className="ff-sr">
        <caption>Responses per day</caption>
        <thead>
          <tr><th scope="col">Date</th><th scope="col">Starts</th><th scope="col">Responses</th></tr>
        </thead>
        <tbody>
          {series.map((day) => (
            <tr key={day.date}>
              <th scope="row">{day.date}</th>
              <td>{day.starts}</td>
              <td>{day.responses}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

/**
 * Where people stopped, page by page.
 *
 * Bars on one scale, longest first by construction — each page can only be reached by
 * people who got through the one before it — so the drop is the difference in length and
 * needs no second encoding. The count that matters is printed on every step.
 */
function Funnel({ funnel }: { funnel: NonNullable<FormDashboard['funnel']> }) {
  const start = funnel.steps[0]?.reached ?? 0;
  if (start === 0) return null;

  return (
    <Card className="p-4">
      <h2 className="mb-3 text-xs font-semibold text-[var(--ff-text-muted)]">
        How far people got
      </h2>

      <ol className="space-y-2">
        {funnel.steps.map((step) => (
          <li key={step.ref}>
            <div className="mb-0.5 flex items-baseline justify-between gap-3 text-xs">
              <span className="min-w-0 truncate">{step.title}</span>
              <span className="shrink-0 tabular-nums text-[var(--ff-text-muted)]">
                {step.reached} · {Math.round((step.reached / start) * 100)}%
                {step.droppedHere > 0 && (
                  <span className="ml-2 text-[var(--ff-danger)]">
                    −{step.droppedHere}
                  </span>
                )}
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded"
                 style={{ background: 'var(--ff-viz-track)' }}>
              <div
                className="h-full rounded"
                style={{
                  width: `${(step.reached / start) * 100}%`,
                  background: 'var(--ff-viz-1)',
                }}
              />
            </div>
          </li>
        ))}
      </ol>

      <p className="mt-2 text-[11px] text-[var(--ff-text-muted)]">
        {funnel.completed} of {start} finished. {funnel.note}
      </p>

      <table className="ff-sr">
        <caption>Sessions reaching each page</caption>
        <thead>
          <tr><th scope="col">Page</th><th scope="col">Reached</th><th scope="col">Stopped here</th></tr>
        </thead>
        <tbody>
          {funnel.steps.map((step) => (
            <tr key={step.ref}>
              <th scope="row">{step.title}</th>
              <td>{step.reached}</td>
              <td>{step.droppedHere}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

/**
 * Sessions by device, with the completion rate for each.
 *
 * The split alone is not the interesting number — it mostly tells you what everybody
 * already knows about their audience. The rate beside it is: a form that finishes at 70%
 * on a desktop and 20% on a phone has a layout problem, and nothing else on this page
 * would show it.
 */
function Devices({ devices }: { devices: FormDashboard['devices'] }) {
  const total = devices.reduce((sum, d) => sum + d.starts, 0);
  if (total === 0) return null;

  return (
    <Card className="p-4">
      <h2 className="mb-3 text-xs font-semibold text-[var(--ff-text-muted)]">
        Device
      </h2>

      <ul className="space-y-2">
        {devices.map((device) => (
          <li key={device.key}>
            <div className="mb-0.5 flex items-baseline justify-between gap-3 text-xs">
              <span>{device.label}</span>
              <span className="shrink-0 tabular-nums text-[var(--ff-text-muted)]">
                {device.starts} started · {device.completionRate}% finished
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded"
                 style={{ background: 'var(--ff-viz-track)' }}>
              <div
                className="h-full rounded"
                style={{
                  width: `${(device.starts / total) * 100}%`,
                  background: 'var(--ff-viz-1)',
                }}
              />
            </div>
          </li>
        ))}
      </ul>

      <table className="ff-sr">
        <caption>Sessions by device</caption>
        <thead>
          <tr>
            <th scope="col">Device</th><th scope="col">Started</th>
            <th scope="col">Completed</th><th scope="col">Completion rate</th>
          </tr>
        </thead>
        <tbody>
          {devices.map((device) => (
            <tr key={device.key}>
              <th scope="row">{device.label}</th>
              <td>{device.starts}</td>
              <td>{device.completions}</td>
              <td>{device.completionRate}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

/* ---- widgets --------------------------------------------------------------------- */

function Widget({ widget, formId }: { widget: DashboardWidget; formId: string }) {
  return (
    <Card className="p-4">
      <div className="mb-2 flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <Icon
          name={iconFor(widget.type)}
          size={14}
          className="translate-y-0.5 text-[var(--ff-text-muted)]"
        />
        <h3 className="text-sm font-medium">{widget.title}</h3>
        <code className="font-mono text-[10px] text-[var(--ff-text-muted)]">
          {widget.questionRef}
        </code>
        {widget.pii && <Badge tone="warning">personal data</Badge>}

        {/* The answer rate is the number that says which question people give up on, and it
            was stored from the beginning without ever being shown. */}
        {widget.answerRate != null && (
          <span className="ml-auto text-xs tabular-nums text-[var(--ff-text-muted)]">
            {widget.answered} answered · {Math.round(widget.answerRate * 100)}%
          </span>
        )}
      </div>

      <Body widget={widget} formId={formId} />

      {widget.caveats.length > 0 && (
        <ul className="mt-2.5 space-y-1">
          {widget.caveats.map((caveat) => (
            <li key={caveat}
                className="flex gap-1.5 text-[11px] leading-relaxed text-[var(--ff-text-muted)]">
              <Icon name="info" size={12} className="mt-0.5 shrink-0" />
              {caveat}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function Body({ widget, formId }: { widget: DashboardWidget; formId: string }) {
  switch (widget.kind) {
    case 'bar':
    case 'orderedBar':
    case 'histogram':
    case 'timeline':
      return (
        <>
          <Bars widget={widget} />
          {/* A scale carries both: the shape, and the average somebody will ask for. */}
          {widget.numeric && <div className="mt-3"><Numeric widget={widget} /></div>}
        </>
      );

    case 'nps':
      return <NpsBreakdown widget={widget} />;

    case 'matrix':
      return <MatrixTable widget={widget} />;

    case 'numeric':
      return <Numeric widget={widget} />;

    case 'textSamples':
      return (
        <p className="text-sm text-[var(--ff-text-muted)]">
          {widget.answered ?? 0} written{' '}
          {(widget.answered ?? 0) === 1 ? 'answer' : 'answers'}.{' '}
          {/* Free text is a list, not a chart. The useful thing is the text, so the widget
              is a way through to it rather than a picture of its word frequencies. */}
          <Link
            to={`/responses?formId=${formId}&questionRef=${widget.questionRef}`}
            className="text-[var(--ff-primary)] hover:underline"
          >
            Read them in Responses
          </Link>
        </p>
      );

    case 'files':
      return (
        <p className="text-sm text-[var(--ff-text-muted)]">
          {widget.answered ?? 0} {(widget.answered ?? 0) === 1 ? 'upload' : 'uploads'}.
        </p>
      );

    case 'notSummarised':
      return (
        <p className="text-sm text-[var(--ff-text-muted)]">
          {widget.answered ?? 0}{' '}
          {(widget.answered ?? 0) === 1 ? 'answer' : 'answers'} recorded — not summarised yet.
        </p>
      );

    case 'countOnly':
    default:
      return (
        <p className="text-sm text-[var(--ff-text-muted)]">
          {widget.answered ?? 0}{' '}
          {(widget.answered ?? 0) === 1 ? 'answer' : 'answers'}.
        </p>
      );
  }
}

/**
 * A single-series horizontal bar chart.
 *
 * One series, so no legend and no colour coding — the title names what is being counted
 * and every bar is labelled with its own value. Colour here carries no information at all,
 * which is exactly why it is one colour.
 */
function Bars({ widget }: { widget: DashboardWidget }) {
  const rows = widget.data ?? [];
  const max = Math.max(...rows.map((r) => r.count ?? 0), 1);

  if (rows.length === 0) {
    return <p className="text-sm text-[var(--ff-text-muted)]">No answers yet.</p>;
  }

  return (
    <figure className="m-0">
      <ul className="space-y-1.5">
        {rows.map((row) => <Bar key={row.key} row={row} max={max} />)}
      </ul>

      <figcaption className="mt-2 text-[11px] text-[var(--ff-text-muted)]">
        {/* Multi-select is why this line exists: percentages of selections sum past 100%
            and confuse everyone who is not an analyst, so the denominator is stated. */}
        Percentages are of {widget.denominator ?? 'answers'}
        {widget.answered != null && ` (${widget.answered})`}.
      </figcaption>

      {/* The bars are labelled, so the numbers are already readable — but a chart is never
          the only way to reach them (docs/17). */}
      <table className="ff-sr">
        <caption>{widget.title}</caption>
        <thead>
          <tr><th scope="col">Answer</th><th scope="col">Count</th><th scope="col">Share</th></tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <th scope="row">{row.label}</th>
              <td>{row.count ?? 'hidden'}</td>
              <td>{row.percent == null ? '—' : `${row.percent}%`}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}

function Bar({ row, max }: { row: DashboardBar; max: number }) {
  const suppressed = row.count == null;

  return (
    <li>
      <div className="mb-0.5 flex items-baseline justify-between gap-3 text-xs">
        <span className={clsx('min-w-0 truncate', row.key === '__other'
          && 'italic text-[var(--ff-text-muted)]')}>
          {row.label}
        </span>
        <span className="shrink-0 tabular-nums text-[var(--ff-text-muted)]">
          {suppressed
            ? 'hidden'
            : <>{row.count}{row.percent != null && ` · ${row.percent}%`}</>}
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded"
           style={{ background: 'var(--ff-viz-track)' }}>
        <div
          className="h-full rounded"
          style={{
            width: suppressed ? '0%' : `${((row.count ?? 0) / max) * 100}%`,
            background: 'var(--ff-viz-1)',
          }}
        />
      </div>
    </li>
  );
}

/**
 * Promoters, passives and detractors as one bar, with the score beside it.
 *
 * A stacked bar rather than three: the parts are shares of the same whole, and the score is
 * the difference between two of them. A gauge dial was the obvious alternative and says
 * less — the dial is decoration, the number is the answer.
 */
function NpsBreakdown({ widget }: { widget: DashboardWidget }) {
  const n = widget.nps;
  if (!n) return <p className="text-sm text-[var(--ff-text-muted)]">No answers yet.</p>;

  const parts = [
    { label: 'Promoters', hint: '9–10', count: n.promoters, color: 'var(--ff-viz-3)' },
    { label: 'Passives', hint: '7–8', count: n.passives, color: 'var(--ff-viz-track)' },
    { label: 'Detractors', hint: '0–6', count: n.detractors, color: 'var(--ff-viz-2)' },
  ];

  return (
    <figure className="m-0">
      <div className="mb-2 flex items-baseline gap-2">
        <span className="text-2xl font-semibold tabular-nums">
          {n.score > 0 ? `+${n.score}` : n.score}
        </span>
        <span className="text-xs text-[var(--ff-text-muted)]">
          NPS, from {n.total} {n.total === 1 ? 'answer' : 'answers'}
        </span>
      </div>

      <div className="flex h-3 gap-0.5 overflow-hidden rounded">
        {parts.filter((p) => p.count > 0).map((part) => (
          <div
            key={part.label}
            style={{ flex: part.count, background: part.color }}
            title={`${part.label}: ${part.count}`}
          />
        ))}
      </div>

      <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
        {parts.map((part) => (
          <li key={part.label} className="flex items-center gap-1.5 text-xs">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: part.color }} />
            <span>{part.label}</span>
            <span className="text-[var(--ff-text-muted)]">{part.hint}</span>
            <span className="tabular-nums font-medium">{part.count}</span>
            <span className="tabular-nums text-[var(--ff-text-muted)]">
              {Math.round((part.count / n.total) * 100)}%
            </span>
          </li>
        ))}
      </ul>

      <figcaption className="mt-2 text-[11px] text-[var(--ff-text-muted)]">
        Promoters minus detractors, as a share of everyone who answered. Passives count
        towards the total and not towards the score.
      </figcaption>
    </figure>
  );
}

/**
 * One 100% stacked bar per matrix row.
 *
 * Rows are the comparison — "which statement scored worst" is the only reason a matrix gets
 * asked — so they stack to the same width and can be read straight down. Counts sit in the
 * table underneath rather than on the segments, which are too narrow to label honestly.
 */
function MatrixTable({ widget }: { widget: DashboardWidget }) {
  const m = widget.matrix;
  if (!m || m.rows.length === 0) {
    return <p className="text-sm text-[var(--ff-text-muted)]">No answers yet.</p>;
  }

  const colour = (index: number) => `var(--ff-viz-${(index % 8) + 1})`;

  return (
    <figure className="m-0">
      <ul className="mb-3 flex flex-wrap gap-x-3 gap-y-1">
        {m.columns.map((column, index) => (
          <li key={column.key} className="flex items-center gap-1.5 text-xs">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: colour(index) }} />
            {column.label}
          </li>
        ))}
      </ul>

      <div className="space-y-2">
        {m.rows.map((row) => (
          <div key={row.ref}>
            <div className="mb-0.5 flex items-baseline justify-between gap-3 text-xs">
              <span className="min-w-0 truncate">{row.label}</span>
              <span className="shrink-0 tabular-nums text-[var(--ff-text-muted)]">
                {row.total}
              </span>
            </div>
            <div className="flex h-3 gap-0.5 overflow-hidden rounded">
              {row.cells.filter((c) => c.count > 0).map((cell) => (
                <div
                  key={cell.key}
                  style={{
                    flex: cell.count,
                    background: colour(m.columns.findIndex((c) => c.key === cell.key)),
                  }}
                  title={`${row.label} — ${cell.label}: ${cell.count} (${cell.percent}%)`}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Colour is never the only encoding: the counts are all here in text. */}
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-xs">
          <caption className="ff-sr">{widget.title}, counts per row and column</caption>
          <thead>
            <tr className="border-b border-[var(--ff-border)]">
              <th scope="col" className="py-1 pr-3 text-left font-medium
                                         text-[var(--ff-text-muted)]">Row</th>
              {m.columns.map((column) => (
                <th key={column.key} scope="col"
                    className="py-1 pl-3 text-right font-medium text-[var(--ff-text-muted)]">
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {m.rows.map((row) => (
              <tr key={row.ref} className="border-b border-[var(--ff-border)] last:border-0">
                <th scope="row" className="py-1 pr-3 text-left font-normal">{row.label}</th>
                {m.columns.map((column) => {
                  const cell = row.cells.find((c) => c.key === column.key);
                  return (
                    <td key={column.key} className="py-1 pl-3 text-right tabular-nums">
                      {cell?.count ?? 0}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  );
}

function Numeric({ widget }: { widget: DashboardWidget }) {
  const n = widget.numeric;
  if (!n) return <p className="text-sm text-[var(--ff-text-muted)]">No answers yet.</p>;

  return (
    <dl className="grid grid-cols-2 gap-3 sm:grid-cols-5">
      <Figure label="Answers" value={n.count} />
      <Figure label="Mean" value={n.mean} />
      {/* From the stored sum of squares, so the same figure is answerable over any range of
          days by adding them rather than by re-reading the responses. */}
      <Figure label="Std dev" value={n.stdDev} />
      <Figure label="Min" value={n.min} />
      <Figure label="Max" value={n.max} />
    </dl>
  );
}

function Figure({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <dt className="text-[11px] text-[var(--ff-text-muted)]">{label}</dt>
      <dd className="text-sm font-medium tabular-nums">{value}</dd>
    </div>
  );
}

/* ---- plumbing -------------------------------------------------------------------- */

const TYPE_ICONS: Record<string, IconName> = {
  short_text: 'text', long_text: 'paragraph', email: 'at', phone: 'phone', url: 'link',
  number: 'hash', currency: 'currency', password: 'shield',
  single_select: 'radio', multi_select: 'checkbox', dropdown: 'chevronDown',
  searchable_dropdown: 'search', image_choice: 'image', yes_no: 'toggle',
  likert: 'scale', rating: 'star', nps: 'gauge', slider: 'slider', star: 'star',
  emoji_scale: 'scale', ranking: 'rank', matrix_single: 'grid', matrix_multi: 'gridCheck',
  date: 'calendar', time: 'clock', datetime: 'calendarClock', country: 'globe',
  file_upload: 'upload', signature: 'signature', calculated: 'function',
};

const iconFor = (type?: string | null): IconName => (type && TYPE_ICONS[type]) || 'text';

/** A day offset from today, as the whole-day string the endpoint expects. */
function isoDay(offset: number): string {
  const date = new Date();
  date.setDate(date.getDate() + offset);
  return date.toISOString().slice(0, 10);
}
